import { get, noop } from 'lodash'
import { useToast } from '@chakra-ui/react'

import { axios, getErrorMsg } from '@/lib/helpers'
import { logEvent } from '@/lib/stats'

import { useCheckoutStore } from '../stores'

export const useCheckoutServices = () => {
  const toast = useToast()

  const {
    hostUrl,
    packageData,
    addressType,
    setRawAddresses,
    selectedAddress,
    recurringType,
  } = useCheckoutStore()

  return {
    createAddress: async (values, payload = {}) => {
      const { onSuccess = noop } = payload

      try {
        const addressResponse = await createAddress({ ...values, addressType })

        setRawAddresses(addressResponse)
        onSuccess()
        toast({
          title: 'Address Created.',
          status: 'success',
          position: 'top-right',
          variant: 'left-accent',
        })
      } catch (error) {
        toast({
          title: getErrorMsg(error),
          status: 'error',
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
        })
      }
    },
    updateAddress: async (values, payload = {}) => {
      const { onSuccess = noop } = payload

      try {
        const addressResponse = await updateAddress({ ...values, addressType })

        setRawAddresses(addressResponse)
        onSuccess()
        toast({
          title: 'Address Updated.',
          status: 'success',
          position: 'top-right',
          variant: 'left-accent',
        })
      } catch (error) {
        toast({
          title: getErrorMsg(error),
          status: 'error',
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
        })
      }
    },
    deleteAddress: async (id) => {
      try {
        const addressResponse = await deleteAddress(id)

        setRawAddresses(addressResponse)
        toast({
          title: 'Address Deleted.',
          status: 'success',
          position: 'top-right',
          variant: 'left-accent',
        })
      } catch (error) {
        toast({
          title: getErrorMsg(error),
          status: 'error',
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
        })
      }
    },
    saveInformation: async ({ onSuccess = noop, onError = noop }) => {
      try {
        const data = {
          package: packageData.id,
          addressId: selectedAddress,
          recurring: recurringType,
        }

        const token = await saveInformation(data)

        logEvent({
          fb: { event: 'InitiateCheckout' },
          ga: {
            category: 'Payment',
            action: 'InitiateCheckout',
          },
        })

        onSuccess()

        window.location.href = `${hostUrl}/payments/${token}`
      } catch (error) {
        toast({
          title: getErrorMsg(error),
          status: 'error',
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
        })
        onError()
      }
    },
    createManualTransaction: async ({ payload, onSuccess = noop, onError = noop }) => {
      try {
        const data = {
          package: packageData.name,
          period: recurringType,
          amount: packageData.price,
          payment_type: 'manual',
          name: payload.name,
          address: payload.address,
          taxpayer_id: payload.taxId,
          wht: payload.taxPayerType === 'company',
        }

        const res = await apiCreateManualTransaction(data)

        logEvent({
          ga: {
            category: 'Payment',
            action: 'ManualTransaction',
          },
        })

        onSuccess()

        window.location.href = `/transactions/${res.data["id"]}/bank_transfer_detail`
      } catch (error) {
        toast({
          title: getErrorMsg(error),
          status: 'error',
          position: 'top-right',
          variant: 'left-accent',
          isClosable: true,
        })
        onError()
      }
    },
  }
}

const createAddress = async (payload) => {
  return axios
    .post('/addresses', {
      name: payload.name,
      details: payload.address,
      primary: payload.primary,
      taxpayer_id: payload.taxId || payload.tax_id,
      taxpayer_type: payload.addressType,
      email: payload.email,
      phone_number: payload.phone,
      tax_id_type: payload.tax_id_type,
      branch_type: payload.branch_type,
      branch_name: payload.branch_name,
      sub_district: payload.sub_district,
      district: payload.district,
      province: payload.province,
      zip_code: payload.zip_code,
    })
    .then(({ data }) => data || [])
}

const updateAddress = (values) => {
  return axios
    .put(`/addresses/${values.id}`, {
      name: values.name,
      details: values.address,
      primary: values.primary,
      taxpayer_id: values.taxId || values.tax_id,
      taxpayer_type: values.addressType,
      email: values.email,
      phone_number: values.phone,
      tax_id_type: values.tax_id_type,
      branch_type: values.branch_type,
      branch_name: values.branch_name,
      sub_district: values.sub_district,
      district: values.district,
      province: values.province,
      zip_code: values.zip_code,
    })
    .then(({ data }) => data || [])
}

const deleteAddress = (id) => {
  return axios.delete(`/addresses/${id}`).then(({ data }) => data || [])
}

const saveInformation = (data) => {
  return axios
    .post('/invoices', {
      address_id: data.addressId,
      package: data.package,
      recurring: data.recurring,
    })
    .then(({ data }) => get(data, 'token'))
}

const apiCreateManualTransaction = (data) => {
  return axios.post('/transactions', data).then(({ data }) => data)
}
